// The Library grid's geometry, computed instead of measured: every card's
// rect from the container width, the column count and each card's ratio.
//
// Computing it (rather than letting CSS columns flow) is what makes the
// grid virtualizable (we know where every tile sits without mounting it)
// and what makes the pinch reflow a FLIP (old rect and new rect for every
// card, mounted or not). Pure: no DOM, no React.

export const MIN_COLUMNS = 2;
export const MAX_COLUMNS = 5;
export const DEFAULT_COLUMNS = 2;

/** Title line under a picture tile at 2 columns: 8px gap + one 18px line. */
export const TITLE_LINE_HEIGHT = 26;

export interface GridDensity {
  columns: number;
  /** Space between tiles, both axes. */
  gap: number;
  /** Corner radius of a tile's picture. */
  radius: number;
  /** Left and right padding of the grid. */
  padding: number;
  /** Titles show under picture tiles at 2 columns only. */
  showTitles: boolean;
}

const DENSITIES: Record<number, Omit<GridDensity, 'columns'>> = {
  2: { gap: 16, radius: 14, padding: 16, showTitles: true },
  3: { gap: 10, radius: 12, padding: 12, showTitles: false },
  4: { gap: 6, radius: 10, padding: 8, showTitles: false },
  5: { gap: 4, radius: 8, padding: 6, showTitles: false },
};

export function clampColumns(columns: number): number {
  if (!Number.isFinite(columns)) return DEFAULT_COLUMNS;
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, Math.round(columns)));
}

export function densityFor(columns: number): GridDensity {
  const c = clampColumns(columns);
  return { columns: c, ...DENSITIES[c] };
}

/** What the layout needs to know about a card. LibraryCard satisfies it. */
export interface LayoutCard {
  id: string;
  aspectRatio: number;
  title: string;
  thumbnail: string | null;
  /** Whether the tile draws a title line under it; defaults to having a title and a picture. */
  showsTitle?: boolean;
}

export interface TileRect {
  id: string;
  index: number;
  column: number;
  x: number;
  y: number;
  width: number;
  /** Whole tile, title line included. */
  height: number;
  /** The picture alone (width / aspectRatio). */
  mediaHeight: number;
}

export interface GridLayout {
  rects: TileRect[];
  byId: Map<string, TileRect>;
  /** Total content height, bottom padding not included. */
  height: number;
  columnWidth: number;
  /** Tallest single tile, for range queries. */
  maxTileHeight: number;
  density: GridDensity;
  width: number;
}

export interface LayoutOptions {
  width: number;
  columns: number;
  /** Overrides for the density table (tests, special grids). */
  gap?: number;
  padding?: number;
  showTitles?: boolean;
  /** Space above the first row. */
  top?: number;
}

/**
 * Masonry, shortest column first: each card goes to the column that ends
 * highest (ties go left), so columns end close to level and reading order
 * runs left to right, top to bottom.
 */
export function computeLayout(cards: readonly LayoutCard[], options: LayoutOptions): GridLayout {
  const base = densityFor(options.columns);
  const density: GridDensity = {
    ...base,
    gap: options.gap ?? base.gap,
    padding: options.padding ?? base.padding,
    showTitles: options.showTitles ?? base.showTitles,
  };
  const { columns, gap, padding, showTitles } = density;
  const width = Math.max(0, options.width);
  const top = options.top ?? 0;
  const columnWidth = Math.max(0, (width - padding * 2 - gap * (columns - 1)) / columns);

  const heights = new Array<number>(columns).fill(top);
  const rects: TileRect[] = [];
  const byId = new Map<string, TileRect>();
  let maxTileHeight = 0;

  cards.forEach((card, index) => {
    let column = 0;
    for (let i = 1; i < columns; i++) if (heights[i] < heights[column] - 0.5) column = i;
    const ratio = card.aspectRatio > 0 && Number.isFinite(card.aspectRatio) ? card.aspectRatio : 1;
    const mediaHeight = columnWidth / ratio;
    const titled = card.showsTitle ?? (!!card.title && !!card.thumbnail);
    const height = mediaHeight + (showTitles && titled ? TITLE_LINE_HEIGHT : 0);
    const rect: TileRect = {
      id: card.id,
      index,
      column,
      x: padding + column * (columnWidth + gap),
      y: heights[column],
      width: columnWidth,
      height,
      mediaHeight,
    };
    heights[column] += height + gap;
    if (height > maxTileHeight) maxTileHeight = height;
    rects.push(rect);
    byId.set(card.id, rect);
  });

  const tallest = Math.max(...heights);
  return { rects, byId, height: cards.length ? tallest - gap : top, columnWidth, maxTileHeight, density, width };
}

/**
 * Rects that intersect [top, bottom) in layout coordinates, in index order.
 * Each card lands on the column that ends highest, and that height only
 * grows, so y is non-decreasing with index: binary search the first card
 * that can reach `top`, then walk until a card starts below `bottom`.
 */
export function rectsInRange(layout: GridLayout, top: number, bottom: number): TileRect[] {
  const { rects } = layout;
  const target = top - layout.maxTileHeight - 1;
  let lo = 0;
  let hi = rects.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (rects[mid].y < target) lo = mid + 1;
    else hi = mid;
  }
  const out: TileRect[] = [];
  for (let i = lo; i < rects.length; i++) {
    const r = rects[i];
    if (r.y >= bottom + 1) break;
    if (r.y < bottom && r.y + r.height > top) out.push(r);
  }
  return out;
}

/** The rect under a point, or the nearest one in the point's column. */
export function rectAt(layout: GridLayout, x: number, y: number): TileRect | null {
  let best: TileRect | null = null;
  let bestDistance = Infinity;
  for (const r of rectsInRange(layout, y - layout.columnWidth * 2, y + layout.columnWidth * 2)) {
    const dx = x < r.x ? r.x - x : x > r.x + r.width ? x - r.x - r.width : 0;
    const dy = y < r.y ? r.y - y : y > r.y + r.height ? y - r.y - r.height : 0;
    const d = dx * dx + dy * dy;
    if (d < bestDistance) {
      bestDistance = d;
      best = r;
      if (d === 0) break;
    }
  }
  return best ?? layout.rects[layout.rects.length - 1] ?? null;
}

/**
 * Damped spring progress (0 to 1, starting at rest) sampled into a CSS
 * `linear()` easing, so many tiles can run the brand spring (stiffness 500,
 * damping 38) on the compositor through the Web Animations API.
 */
export function springEasing(stiffness = 500, damping = 38, mass = 1, samples = 48): { easing: string; duration: number; progress: (t: number) => number } {
  const w0 = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));
  const progress = (t: number): number => {
    if (t <= 0) return 0;
    if (zeta < 1) {
      const wd = w0 * Math.sqrt(1 - zeta * zeta);
      return 1 - Math.exp(-zeta * w0 * t) * (Math.cos(wd * t) + ((zeta * w0) / wd) * Math.sin(wd * t));
    }
    if (zeta === 1) return 1 - Math.exp(-w0 * t) * (1 + w0 * t);
    const r1 = -w0 * (zeta - Math.sqrt(zeta * zeta - 1));
    const r2 = -w0 * (zeta + Math.sqrt(zeta * zeta - 1));
    // x(0) = 0 and x'(0) = 0: c1 + c2 = 1, c1 r1 + c2 r2 = 0
    const c1 = r2 / (r2 - r1);
    const c2 = -r1 / (r2 - r1);
    return 1 - (c1 * Math.exp(r1 * t) + c2 * Math.exp(r2 * t));
  };
  // Settle when the envelope is under 0.1% of the travel
  let duration = 0.05;
  while (duration < 2 && Math.abs(1 - progress(duration)) + Math.abs(1 - progress(duration + 0.016)) > 0.002) duration += 0.01;
  const points: string[] = [];
  for (let i = 0; i <= samples; i++) {
    const v = i === samples ? 1 : progress((duration * i) / samples);
    points.push(String(Math.round(v * 10000) / 10000));
  }
  return { easing: `linear(${points.join(', ')})`, duration: Math.round(duration * 1000), progress };
}
